// CostLogForm utility functions
import type {
  CostLogEntry,
  CostLogExistingReport,
  CostTaxClass,
} from "./types";
import { COST_CODE_TAX_DEFAULTS, type CostLogCode1 } from "./constants";
import { generatePrefixedId } from "../../../../lib/id";
import { selectCarryForwardRows } from "../shared/carryForward";

/** DRAFT default IDC/TDC for a cost code (see COST_CODE_TAX_DEFAULTS — accounting-ratification
 *  pending). "" when the code has no seeded default. Pure; used to pre-fill (not force) taxClass. */
export function defaultTaxClassForCode(code1: string): CostTaxClass {
  return COST_CODE_TAX_DEFAULTS[code1 as CostLogCode1] ?? "";
}

/**
 * Line cost = cost/unit × units.
 * FE-MATH-EXEMPT: basic display arithmetic (line-item cost×qty for the cost-log/BI table) — PO-approved 2026-08-06
 * Numbers in a box, not a stamped/allocated figure; ratified carve-out, see CLAUDE.md
 * "Basic display/BI arithmetic carve-out". Returns null when non-numeric so display shows "--".
 */
export function calculateLineCost(
  costPerUnit: string,
  units: string,
): number | null {
  const unitNum = parseFloat(costPerUnit);
  const qtyNum = parseFloat(units);
  if (isNaN(unitNum) || isNaN(qtyNum)) return null;
  return unitNum * qtyNum;
}

/**
 * Format a line cost for display ("--" when not yet computable).
 */
export function formatLineCost(costPerUnit: string, units: string): string {
  const value = calculateLineCost(costPerUnit, units);
  if (value === null) return "--";
  return formatCurrency(value);
}

/**
 * Day total = Σ line costs.
 * FE-MATH-EXEMPT: basic display aggregation (Σ line costs for the cost-log/BI table) — PO-approved 2026-08-06
 * Numbers in a box, not a stamped/allocated figure; ratified carve-out (CLAUDE.md).
 */
export function calculateTotalCost(entries: CostLogEntry[]): number {
  return entries.reduce((sum, entry) => {
    const line = calculateLineCost(entry.costPerUnit, entry.units);
    return sum + (line ?? 0);
  }, 0);
}

/**
 * Format currency value
 */
export function formatCurrency(value: number): string {
  return `$${value.toFixed(2)}`;
}

/**
 * Turn a prior-day cost row flagged carry-forward into a fresh row for the new
 * day. STICKY CHAIN (PO 2026-07-11): the copy keeps `carryForward: true`, so a
 * steady cost keeps rolling to each following day until the user unchecks it or
 * deletes the row. New id; every value field is preserved verbatim (same
 * equipment/rate/coding carried forward).
 */
export function createCarryForwardRow(prev: CostLogEntry): CostLogEntry {
  return {
    id: generatePrefixedId("cost-log"),
    costPerUnit: prev.costPerUnit,
    units: prev.units,
    code1: prev.code1,
    code2: prev.code2,
    interval: prev.interval,
    phase: prev.phase,
    activity: prev.activity,
    comment: prev.comment,
    carryForward: true,
    // Steady line identity carries forward (same vendor / AFE / UOM / rental / tax class for a
    // recurring cost). Invoice # and PO # do NOT carry — they are per-transaction reconciliation
    // refs assigned to each actual delivery, so a carried accrual starts unreconciled.
    vendorName: prev.vendorName,
    vendorNo: prev.vendorNo,
    afeId: prev.afeId,
    uom: prev.uom,
    isRental: prev.isRental,
    taxClass: prev.taxClass,
  };
}

/** Phantom-record guard: a cost-log day's `data` is "empty" when NO line has content. Fed to
 *  useDailyReportPage (as isEmptyData) by the single-day Cost Log page (CostLogDayScreen) so a fresh day never
 *  persists a phantom record on mount. Reuses hasCostContent so the guard can't drift. */
export function isCostLogEmpty(data: Record<string, unknown>): boolean {
  const entries = (data.entries as CostLogEntry[] | undefined) ?? [];
  return !entries.some(hasCostContent);
}

/** True when a cost row holds real content (not a blank scaffold row). Mirrors
 *  `isCostLogEmpty` — `units` (defaults to "1") is NOT content. */
export function hasCostContent(e: CostLogEntry): boolean {
  return !!(
    e.costPerUnit ||
    e.code1 ||
    e.code2 ||
    e.interval ||
    e.phase ||
    e.activity ||
    e.comment ||
    // Accounting-only lines are real content too (a back-office reconciler may add a vendor/invoice/
    // AFE/tax line with the cost still pending). UOM / vendorNo / isRental are modifiers, not content.
    e.vendorName ||
    e.invoiceNo ||
    e.poNo ||
    e.afeId ||
    e.taxClass
  );
}

/**
 * Seed a new cost-log day from the immediately-preceding WORKED day's
 * carry-forward rows. See selectCarryForwardRows for the full rule (skips blank
 * scaffold days so a weekend gap doesn't break the chain; unchecking still
 * breaks it; a blank flagged row seeds nothing).
 */
export function getCarryForwardRows(
  existingReports: CostLogExistingReport[],
  activeDayId: string | null,
  activeDate: string | undefined,
): CostLogEntry[] | null {
  return selectCarryForwardRows(existingReports, activeDayId, activeDate, {
    reportType: "cost-log",
    hasContent: hasCostContent,
    copyRow: createCarryForwardRow,
  });
}
